import { gql } from '@apollo/client';
import React, { FunctionComponent } from 'react';
import { calendarIcon, inMemoryIcon, teamIcon } from '@justgiving/icons';
import IconType from '../../constants/iconTypes';
import './Reason.scss';
import {
  ReasonFragment,
  Reason as ReasonType,
  Occasion,
  OccasionType,
} from '../../../types/__generated__';
import usePageQuery, {
  buildQuery,
  UsePageQueryResult,
} from '../../hooks/usePageQuery';
import IconLabel from '../IconLabel/IconLabel';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

const FRAGMENT = buildQuery(gql`
  fragment Reason on Page {
    occasion {
      type
      date
      names
      description
    }
    reason {
      icon
      reason
      reasonUrl
    }
  }
`);

const iconMap = {
  [IconType.CalendarIcon]: calendarIcon,
  [IconType.InMemoryIcon]: inMemoryIcon,
  [IconType.TeamIcon]: teamIcon,
};

export const Reason: FunctionComponent = () => {
  const result = usePageQuery<ReasonFragment>(FRAGMENT);
  const { t } = useTranslation('common', { useSuspense: false });

  if (!UsePageQueryResult.hasData(result)) {
    return null;
  }

  const { occasion, reason } = result.data;

  if (occasion) {
    const label = renderOccasion(occasion, t);

    if (!label) {
      return null;
    }

    return (
      <div data-qa="reason-details">
        <IconLabel icon={calendarIcon} label={label} />
      </div>
    );
  } else if (reason) {
    const icon = reason.icon ? iconMap[reason.icon] : '';

    return (
      <div data-qa="reason-details">
        <IconLabel icon={icon} label={renderReason(reason)} />
      </div>
    );
  }

  return null;
};

function renderReason(reason: ReasonType): string | JSX.Element {
  if (!reason.reasonUrl) {
    return reason.reason ?? '';
  }

  return <a href={reason.reasonUrl}>{reason.reason}</a>;
}

function renderOccasion(occasion: Occasion, t: TFunction): string | null {
  const date = occasion.date
    ? new Date(occasion.date).getTime().toString()
    : null;

  switch (occasion.type) {
    case OccasionType.Birthday: {
      const name = occasion.names?.[0];

      if (!name) {
        return null;
      }

      if (occasion.date) {
        return t('occasion.birthday.withDateLabel', {
          name,
          date,
        });
      } else {
        return t('occasion.birthday.withoutDateLabel', { name });
      }
    }
    case OccasionType.Wedding: {
      const name1 = occasion.names?.[0];
      const name2 = occasion.names?.[1];

      if (!name1 || !name2) {
        return null;
      }

      if (occasion.date) {
        return t('occasion.wedding.withDateLabel', {
          name1,
          name2,
          date,
        });
      } else {
        return t('occasion.wedding.withoutDateLabel', { name1, name2 });
      }
    }
    case OccasionType.Other: {
      const description = occasion.description;

      if (!description) {
        return null;
      }

      if (occasion.date) {
        return t('occasion.other.withDateLabel', {
          description,
          date,
        });
      } else {
        return t('occasion.other.withoutDateLabel', { description });
      }
    }
    default:
      return null;
  }
}

export default Reason;
